// Laser Add Sheet: the laser camera records sheets (jobq-api "LASER CAMERA SHEETS", laser_camera.py, 2026-11-03).
//
// Mike: "I want the camera to be used to enforce accurate recording of a sheet. It sounds like operators are
// clicking add sheet randomly, not when the sheet is actually swapped. There only needs to be an override when
// the camera misses something or isn't operational."
//
// The API owns the rule. With its switch on (app_settings laser_camera_mode = 'on'), the Amada bed camera live
// and a sheet that comes in on the shuttle, the camera records sheet 2 onward of the job on the laser; a tap
// for one of those sheets is refused (409 `camera_records_sheets`) unless the camera saw a swap nobody recorded.
// A manager can record a sheet the camera missed (camera_override_pin + camera_override_reason).
// GET /laser/{id}/camera says what the camera is doing for the screen.
//
// This file is the screen's read + rules (no React); `useLaserCamera.js` polls the read. Mode off (the API's
// default, and the answer of an API without the endpoint) shows nothing: the screen is exactly as before.
import { beaconFetch } from '../../utils/beacons';
import { visibleLen } from '../../utils/tmPricing';

/** How often the laser screen re-reads the camera while a Laser task is open. */
export const CAMERA_POLL_MS = 5000;

/** Failed reads in a row (401, 429, 5xx, no answer) after which the last answer is no longer shown. */
export const CAMERA_FAILS_BEFORE_CLEAR = 3;

/** The statuses that mean "nothing to poll here": no such endpoint, a non-Laser task (400), an unknown task (404). */
export const CAMERA_MISSING_STATUSES = Object.freeze([400, 404, 405]);

/** The API's minimum for an override reason (tm_pricing.visible_len >= 5). */
export const CAMERA_OVERRIDE_REASON_MIN = 5;

/** The API's name for the override (laser_camera.OVERRIDE_WHAT); its 400 for a short reason starts with it. */
export const CAMERA_OVERRIDE_WHAT = 'Recording a sheet the camera missed';

/**
 * GET /laser/{task_id}/camera. Never throws. Resolves
 *  - {camera}               an answer (a JSON object),
 *  - {missing: true}        nothing to poll (400 / 404 / 405): polling stops,
 *  - {failed: true, status} anything else: keep what we had for a while, keep polling.
 */
export async function loadLaserCamera(taskId) {
  const r = await beaconFetch(`/laser/${encodeURIComponent(taskId)}/camera`);
  if (r.ok && r.body && typeof r.body === 'object' && !Array.isArray(r.body)) return { camera: r.body };
  if (CAMERA_MISSING_STATUSES.includes(r.status)) return { missing: true };
  return { failed: true, status: r.status };
}

/** The switch is on (the only state in which the screen shows anything). */
export function cameraOn(camera) {
  return !!(camera && typeof camera === 'object' && camera.mode === 'on');
}

const TONES = Object.freeze({
  unrecorded: 'warn',
  offline: 'muted',
  not_visible: 'muted',
  first_sheet: 'info',
  pending: 'info',
  counting: 'info',
});

/**
 * The camera line: {tone: 'warn' | 'muted' | 'info', status, text}, or null (mode off, no answer, or no
 * message: off / done / all_recorded). `message` is the API's sentence, shown as is.
 */
export function cameraLineView(camera) {
  if (!cameraOn(camera)) return null;
  const text = typeof camera.message === 'string' ? camera.message.trim() : '';
  if (!text) return null;
  const status = typeof camera.status === 'string' ? camera.status : null;
  return { tone: TONES[status] || 'info', status, text };
}

/** The colours of a camera line's tone (inline styles; amber warning, grey, blue information). */
export function cameraToneStyle(tone) {
  if (tone === 'warn') {
    return { backgroundColor: 'var(--jq-warning-bg, #fff3cd)', border: '1px solid var(--jq-warning-border, #ffe69c)',
             color: 'var(--jq-warning-text, #664d03)' };
  }
  if (tone === 'muted') {
    return { backgroundColor: 'var(--jq-bg-subtle, #e9ecef)', border: '1px solid var(--jq-border-medium, #ccc)',
             color: 'var(--jq-text-muted, #666)' };
  }
  return { backgroundColor: '#e7f1ff', border: '1px solid #9ec5fe', color: '#052c65' };
}

function count(n) {
  if (n === null || n === undefined || n === '' || typeof n === 'boolean') return null;
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
}

/**
 * The camera recorded a sheet the screen does not show yet: the answer (for this task, switch on) counts more
 * sheets than `shownCount`. The screen then reloads the task the way a successful Add Sheet does.
 */
export function cameraRecordedSheet(camera, shownCount, taskId = null) {
  if (!cameraOn(camera)) return false;
  if (taskId !== null && taskId !== undefined && camera.task_id !== undefined && camera.task_id !== null
      && String(camera.task_id) !== String(taskId)) {
    return false;
  }
  const polled = count(camera.sheet_count);
  if (polled === null) return false;
  const shown = count(shownCount);
  return polled > (shown === null ? 0 : shown);
}

/** Two answers that draw and decide the same (an unchanged answer re-renders nothing). */
export function sameCameraAnswer(a, b) {
  if (a === b) return true;
  try { return JSON.stringify(a) === JSON.stringify(b); } catch (_) { return false; }
}

/**
 * The save's 409 refusal (increment_sheet detail {error: 'camera_records_sheets', reason, sheet_count, message,
 * override: {what}}) from the response text (or a parsed body), or null for any other answer. `override` is
 * null when the API offers none (the race "The camera just recorded this sheet. Nothing was added.").
 */
export function cameraRefusal(text) {
  try {
    const body = typeof text === 'string' ? JSON.parse(text) : text;
    const d = body && body.detail;
    if (d && typeof d === 'object' && d.error === 'camera_records_sheets') {
      const ov = d.override && typeof d.override === 'object' ? d.override : null;
      return {
        reason: d.reason || null,
        sheetCount: count(d.sheet_count),
        message: (typeof d.message === 'string' && d.message.trim()) || 'The camera records this sheet when it goes on the laser.',
        override: ov ? { what: (typeof ov.what === 'string' && ov.what.trim()) || CAMERA_OVERRIDE_WHAT } : null,
      };
    }
  } catch (_) { /* not JSON */ }
  return null;
}

/**
 * The manager override's fields, or {error} to show before anything is sent: a manager PIN and a reason of
 * at least 5 visible characters.
 */
export function cameraOverrideFields(managerPin, reason) {
  const pin = String(managerPin || '').trim();
  const why = String(reason || '').trim();
  if (!pin) return { error: 'A manager PIN is required to record a sheet the camera missed.' };
  if (visibleLen(why) < CAMERA_OVERRIDE_REASON_MIN) {
    return { error: `Say what the camera missed (at least ${CAMERA_OVERRIDE_REASON_MIN} characters).` };
  }
  return { cameraOverridePin: pin, cameraOverrideReason: why };
}

function detailText(text) {
  try {
    const body = typeof text === 'string' ? JSON.parse(text) : text;
    const d = body && body.detail;
    return typeof d === 'string' && d.trim() ? d.trim() : null;
  } catch (_) {
    return null;
  }
}

/**
 * A save that carried the camera override and failed because of it: the API's sentence to show in the
 * override box, else null (the save's other answers are handled as before).
 *  - 401 (the manager PIN not recognised, a face token) and 403 (not an Admin or Manager): always the override's;
 *  - 400: only the override's reason refusal ("Recording a sheet the camera missed needs a reason of ...").
 */
export function cameraOverrideFailure(status, text, what = CAMERA_OVERRIDE_WHAT) {
  if (status !== 400 && status !== 401 && status !== 403) return null;
  const d = detailText(text);
  if (status === 400) {
    const name = String(what || CAMERA_OVERRIDE_WHAT).toLowerCase();
    return d && d.toLowerCase().startsWith(name) ? d : null;
  }
  return d || (status === 403 ? 'Only an Admin or Manager can record a sheet the camera missed.'
    : "The manager PIN wasn't recognised. Nothing was added.");
}
